import React, { useState } from 'react';
import { Modal, Button, Form, Row, Col, Spinner, Card } from 'react-bootstrap';
import { LuSparkles, LuCheck, LuRefreshCw } from 'react-icons/lu';
import AxiosService from '../../../../services/AxiosHelper';
import { useDispatch } from 'react-redux';
import { uploadBusinessLogo } from '../../../../redux/reducers/businessSlice';
import { toast } from 'react-toastify';

const AiLogoModal = ({ show, onHide, business }) => {
    const dispatch = useDispatch();
    const [businessName, setBusinessName] = useState(business?.name || '');
    const [businessType, setBusinessType] = useState(business?.businessType || '');
    const [color, setColor] = useState('#000000ff');
    const [loading, setLoading] = useState(false);
    const [logos, setLogos] = useState([]);
    const [selectedLogo, setSelectedLogo] = useState(null);
    const [uploading, setUploading] = useState(false);

    const baseURL = process.env.REACT_APP_BASEURL;

    const handleGenerate = async () => {
        if (!businessName || !businessType) {
            toast.error("Please provide both business name and type.");
            return;
        }

        setLoading(true);
        setSelectedLogo(null);
        try {
            const response = await AxiosService.AxiostRequest({
                method: 'post',
                url: `${baseURL}/ai/generate-logos`,
                data: { businessName, businessType, color }
            });

            if (response.data.success) {
                setLogos(response.data.logos);
            } else {
                toast.error(response.data.message || "Failed to generate logos");
            }
        } catch (error) {
            console.error("AI Generation Error:", error);
            toast.error(error.response?.data?.message || "Error connecting to AI service");
        } finally {
            setLoading(false);
        }
    };

    const base64ToFile = (base64String, fileName) => {
        const arr = base64String.split(',');
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], fileName, { type: mime });
    };

    const handleUseLogo = async () => {
        if (!selectedLogo) return;

        setUploading(true);
        try {
            const file = base64ToFile(selectedLogo, `ai-logo-${Date.now()}.png`);
            await dispatch(uploadBusinessLogo({ id: business._id, file })).unwrap();
            onHide();
        } catch (error) {
            console.error("Logo Upload Error:", error);
        } finally {
            setUploading(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered className="ai-logo-modal">
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title className="fw-bold d-flex align-items-center gap-2">
                    <LuSparkles className="text-brand" style={{ color: '#28B0C4' }} /> AI Logo Generator
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-3">
                <p className="text-muted mb-4">Tell us about your business and we'll generate 5 unique logo options for you.</p>

                <Form className="mb-4">
                    <Row className="g-3">
                        <Col md={5}>
                            <Form.Group>
                                <Form.Label className="small fw-bold">Business Name</Form.Label>
                                <Form.Control 
                                    type="text" 
                                    placeholder="e.g. Manish Studios" 
                                    value={businessName}
                                    onChange={(e) => setBusinessName(e.target.value)}
                                    className="bg-light border-0"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="small fw-bold">Business Type</Form.Label>
                                <Form.Select 
                                    value={businessType}
                                    onChange={(e) => setBusinessType(e.target.value)}
                                    className="bg-light border-0"
                                >
                                    <option value="">Select Type</option>
                                    <option value="food-and-beverage">Food and Beverage</option>
                                    <option value="beauty-and-salon">Beauty and Salon</option>
                                    <option value="medical-and-pharmacy">Medical and Pharmacy</option>
                                    <option value="motor-parts-and-accessories">Motor Parts and Accessories</option>
                                    <option value="mobile-and-accessories">Mobile and Accessories</option>
                                    <option value="other">Other</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label className="small fw-bold">Theme Color</Form.Label>
                                <div className="d-flex gap-2 align-items-center">
                                    <Form.Control 
                                        type="color" 
                                        value={color}
                                        onChange={(e) => setColor(e.target.value)}
                                        className="form-control-color border-0 p-1"
                                        title="Choose your color"
                                    />
                                    <span className="small text-muted">{color}</span>
                                </div>
                            </Form.Group>
                        </Col>
                        <Col md={12} className="text-end">
                            <Button 
                                variant="brand" 
                                onClick={handleGenerate} 
                                disabled={loading || uploading}
                                className="rounded-pill px-4 d-flex align-items-center gap-2 ms-auto"
                                style={{ backgroundColor: '#28B0C4', borderColor: '#28B0C4', color: '#fff' }}
                            >
                                {loading ? (
                                    <>
                                        <Spinner size="sm" /> Generating Logos...
                                    </>
                                ) : (
                                    <>
                                        <LuRefreshCw size={18} /> Generate Options
                                    </>
                                )}
                            </Button>
                        </Col>
                    </Row>
                </Form>

                {logos.length > 0 && (
                    <div className="mt-4">
                        <h6 className="fw-bold mb-3">Choose a logo to use:</h6>
                        <Row className="g-3">
                            {logos.map((logo, index) => (
                                <Col key={index} xs={6} md={4} lg={3}>
                                    <Card 
                                        className={`border-2 cursor-pointer h-100 logo-card ${selectedLogo === logo ? 'border-brand' : 'border-light'}`}
                                        onClick={() => setSelectedLogo(logo)}
                                        style={{ 
                                            cursor: 'pointer', 
                                            transition: 'all 0.2s',
                                            borderColor: selectedLogo === logo ? '#28B0C4' : '#f8f9fa'
                                        }}
                                    >
                                        <div className="position-relative">
                                            <Card.Img src={logo} className="p-2" />
                                            {selectedLogo === logo && (
                                                <div className="position-absolute top-0 end-0 text-white p-1 rounded-circle m-1 shadow-sm" style={{ backgroundColor: '#28B0C4' }}>
                                                    <LuCheck size={12} />
                                                </div>
                                            )}
                                        </div>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="border-0">
                <Button variant="light" onClick={onHide} className="rounded-pill px-4">Cancel</Button>
                <Button 
                    variant="brand" 
                    onClick={handleUseLogo} 
                    disabled={!selectedLogo || uploading}
                    className="rounded-pill px-4"
                    style={{ backgroundColor: '#28B0C4', borderColor: '#28B0C4', color: '#fff' }}
                >
                    {uploading ? <Spinner size="sm" /> : "Use Selected Logo"}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default AiLogoModal;
